var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
const muteTypes = ['comp.gain', 'ctrl.mute'];
const volumeTypes = ['comp.gain', 'ctrl.gain'];
function getChannelCallType(chan) {
    if (chan.type === 'comp.softphone') {
        return 'voip';
    }
    else if (chan.type === 'comp.pots_control_status') {
        return 'pstn';
    }
    return undefined;
}
function getFilters(type) {
    return {
        amplifier: volumeTypes.includes(type),
        assistiveListeningMute: muteTypes.includes(type),
        assistiveListeningMuteStatus: false,
        assistiveListeningVolume: volumeTypes.includes(type),
        audioInputMute: muteTypes.includes(type),
        audioInputMuteStatus: false,
        audioInputVolume: volumeTypes.includes(type),
        mics: muteTypes.includes(type),
        micsMuteStatus: false,
        overflowRoomMute: muteTypes.includes(type),
        overflowRoomMuteStatus: false,
        overflowRoomVolume: volumeTypes.includes(type),
        preset: false,
        receiveGain: [
            'comp.gain',
            'comp.telephony_receiver',
            'comp.io_card_pots_input_for_core',
            'ctrl.gain',
        ].includes(type),
        soundReinforcementMute: muteTypes.includes(type),
        soundReinforcementMuteStatus: false,
        soundReinforcementVolume: volumeTypes.includes(type),
        telephony: ['comp.softphone', 'comp.pots_control_status'].includes(type),
    };
}
export class QSys {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.channels = [];
        this.config = { equipmentId: '', equipmentType: QSys.key, ipAddress: '' };
        this.id = QSys.minId;
        this.receiveGainNames = {};
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                const resp = (yield this.sendQrcCommand('Component.GetComponents', 'test', tags));
                // Some of the result members have Name = plugin_source and their Value is a huge chunk
                // of data.
                this.channels.length = 0;
                if (!resp.result || !Array.isArray(resp.result)) {
                    this.log.error(`invalid response to components query`);
                    return [];
                }
                for (const comp of resp.result) {
                    const type = `comp.${(_a = comp.Type) !== null && _a !== void 0 ? _a : 'undefined'}`;
                    const chan = {
                        filters: getFilters(type),
                        name: (_b = comp.Name) !== null && _b !== void 0 ? _b : 'undefined',
                        type,
                    };
                    if (Array.isArray(comp.Properties) && comp.Type === 'gain') {
                        for (const prop of comp.Properties) {
                            if (prop.Name === 'max_gain' && prop.Value) {
                                chan.maxGain = parseInt(prop.Value, 10);
                            }
                            if (prop.Name === 'min_gain' && prop.Value) {
                                chan.minGain = parseInt(prop.Value, 10);
                            }
                        }
                    }
                    this.channels.push(chan);
                }
                return this.channels.map(chan => ({
                    filters: chan.filters,
                    name: chan.name,
                    type: chan.type,
                }));
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _c, _d, _e, _f, _g;
                const chan = this.channels.find(chan => chan.name === key);
                if (chan === undefined)
                    throw new Error(`invalid key: ${key}`);
                if (chan.type.startsWith('comp')) {
                    const controlName = this.getControlName('mute', chan.type);
                    const resp = (yield this.sendQrcCommand('Component.Get', {
                        Name: key,
                        Controls: [{ Name: controlName }],
                    }, tags));
                    return ((_e = (_d = (_c = resp.result) === null || _c === void 0 ? void 0 : _c.Controls) === null || _d === void 0 ? void 0 : _d[0].Value) !== null && _e !== void 0 ? _e : 0) !== 0;
                }
                else {
                    const resp = (yield this.sendQrcCommand('Control.Get', [key], tags));
                    return ((_g = (_f = resp.result) === null || _f === void 0 ? void 0 : _f[0].Value) !== null && _g !== void 0 ? _g : 0) !== 0;
                }
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const chan = this.channels.find(chan => chan.name === key);
                if (chan === undefined)
                    throw new Error(`invalid key: ${key}`);
                if (chan.type.startsWith('comp')) {
                    const controlName = this.getControlName('mute', chan.type);
                    yield this.sendQrcCommand('Component.Set', {
                        Name: key,
                        Controls: [
                            {
                                Name: controlName,
                                Value: value ? 1 : 0,
                            },
                        ],
                    }, tags);
                }
                else {
                    yield this.sendQrcCommand('Control.Set', {
                        Name: key,
                        Value: value ? 1 : 0,
                    }, tags);
                }
                return value;
            }),
            getKey: (config) => config,
            validate: (config, info) => __awaiter(this, void 0, void 0, function* () {
                return this.validate(config, info.filter);
            }),
        };
        this.telephony = {
            answer: (key) => __awaiter(this, void 0, void 0, function* () {
                yield this.sendQrcCommand('Component.Set', {
                    Name: key,
                    Controls: [{ Name: 'call_connect', Value: 1 }],
                });
                return true;
            }),
            call: (value) => __awaiter(this, void 0, void 0, function* () {
                const key = this.getAvailableCallInterface(value.callType);
                if (key === undefined) {
                    this.log.error(`no available ${value.callType} call interfaces`);
                    return undefined;
                }
                yield this.sendQrcCommand('Component.Set', {
                    Name: key,
                    Controls: [{ Name: 'call_number', Value: value.dialString }],
                });
                yield this.sendQrcCommand('Component.Set', {
                    Name: key,
                    Controls: [{ Name: 'call_connect', Value: 1 }],
                });
                return key;
            }),
            dial: (key, value) => __awaiter(this, void 0, void 0, function* () {
                for (const digit of value) {
                    const control = QSys.digitMap[digit];
                    if (control === undefined) {
                        this.log.error(`invalid digit ${digit} in dial string`);
                        continue;
                    }
                    yield this.sendQrcCommand('Component.Set', {
                        Name: key,
                        Controls: [{ Name: control, Value: 1 }],
                    });
                }
                return value;
            }),
            hangup: (key) => __awaiter(this, void 0, void 0, function* () {
                yield this.sendQrcCommand('Component.Set', {
                    Name: key,
                    Controls: [{ Name: 'call_disconnect', Value: 1 }],
                });
                return false;
            }),
            receiveGainRange: { max: 10, min: -10, round: 1 },
            reject: (key) => __awaiter(this, void 0, void 0, function* () {
                yield this.sendQrcCommand('Component.Set', {
                    Name: key,
                    Controls: [{ Name: 'call_connect', Value: 1 }],
                });
                yield this.sendQrcCommand('Component.Set', {
                    Name: key,
                    Controls: [{ Name: 'call_disconnect', Value: 1 }],
                });
                return false;
            }),
            setReceiveGain: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const name = this.receiveGainNames[key];
                if (name === undefined) {
                    this.log.error(`no receive gain for telephony channel: "${key}"`);
                    return value;
                }
                const chan = this.channels.find(chan => chan.name === name);
                if (chan === undefined)
                    throw new Error(`invalid key: ${key}`);
                if (chan.type.startsWith('comp')) {
                    yield this.sendQrcCommand('Component.Set', {
                        Name: name,
                        Controls: [
                            {
                                Name: this.getControlName('receiveGain', chan.type),
                                Value: value,
                            },
                        ],
                    });
                }
                else {
                    yield this.sendQrcCommand('Control.Set', {
                        Name: name,
                        Value: value,
                    });
                }
                return value;
            }),
            getCalls: () => __awaiter(this, void 0, void 0, function* () {
                const calls = {};
                // Initially report calls as disconnected. Polling will update to the correct states.
                for (const chan of this.channels) {
                    if (chan.type === 'comp.softphone') {
                        calls[chan.name] = this.state.calls.addMissing({
                            callType: 'voip',
                            canDial: true,
                        });
                    }
                    else if (chan.type === 'comp.pots_control_status') {
                        calls[chan.name] = this.state.calls.addMissing({
                            callType: 'pstn',
                            canDial: true,
                        });
                    }
                }
                return calls;
            }),
            getMaxCalls: () => __awaiter(this, void 0, void 0, function* () {
                var _h;
                const maxCalls = {};
                const telConfigs = ((_h = this.config.telephony) !== null && _h !== void 0 ? _h : {});
                for (const telConfig of Object.values(telConfigs)) {
                    const chan = this.channels.find(chan => chan.name === telConfig.softPhoneStatus);
                    if (chan === undefined)
                        continue;
                    if (chan.type === 'comp.softphone') {
                        if (maxCalls.voip === undefined)
                            maxCalls.voip = 0;
                        maxCalls.voip++;
                    }
                    else if (chan.type === 'comp.pots_control_status') {
                        if (maxCalls.pstn === undefined)
                            maxCalls.pstn = 0;
                        maxCalls.pstn++;
                    }
                }
                return maxCalls;
            }),
            getKey: (config) => config.softPhoneStatus,
            validate: (config, info) => __awaiter(this, void 0, void 0, function* () {
                const result = {
                    softPhoneStatus: yield this.validate(config.softPhoneStatus, 'telephony'),
                };
                if (config.receiveGain !== undefined) {
                    this.receiveGainNames[config.softPhoneStatus] = config.receiveGain;
                    result.receiveGain = yield this.validate(config.receiveGain, 'receiveGain');
                }
                return result;
            }),
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _j, _k, _l, _m, _o;
                const chan = this.channels.find(chan => chan.name === key);
                if (chan === undefined)
                    throw new Error(`invalid key: ${key}`);
                if (chan.type.startsWith('comp')) {
                    const controlName = this.getControlName('volume', chan.type);
                    const resp = (yield this.sendQrcCommand('Component.Get', {
                        Name: key,
                        Controls: [{ Name: controlName }],
                    }, tags));
                    return (_l = (_k = (_j = resp.result) === null || _j === void 0 ? void 0 : _j.Controls) === null || _k === void 0 ? void 0 : _k[0].Value) !== null && _l !== void 0 ? _l : 0;
                }
                else {
                    const resp = (yield this.sendQrcCommand('Control.Get', [key], tags));
                    return (_o = (_m = resp.result) === null || _m === void 0 ? void 0 : _m[0].Value) !== null && _o !== void 0 ? _o : 0;
                }
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const chan = this.channels.find(chan => chan.name === key);
                if (chan === undefined)
                    throw new Error(`invalid key: ${key}`);
                if (chan.type.startsWith('comp')) {
                    const controlName = this.getControlName('volume', chan.type);
                    yield this.sendQrcCommand('Component.Set', {
                        Name: key,
                        Controls: [
                            {
                                Name: controlName,
                                Value: value,
                            },
                        ],
                    }, tags);
                }
                else {
                    yield this.sendQrcCommand('Control.Set', {
                        Name: key,
                        Value: value,
                    }, tags);
                }
                if (key === this.volumeChannel) {
                    const newVolumeMute = value <= -38;
                    if (this.state.mutes.values[key] !== newVolumeMute) {
                        const value = yield this.mute.set(key, newVolumeMute, ['set.volumeMute']);
                        this.state.mutes.update(key, value);
                    }
                }
                return value;
            }),
            getKey: (config) => config,
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                return info.filter === 'amplifier'
                    ? { max: 12, min: -38, round: 1 }
                    : { max: 12, min: -12, round: 1 };
            }),
            validate: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (info.filter === 'amplifier')
                    this.volumeChannel = config;
                return this.validate(config, info.filter);
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 3000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    var _p, _q, _r, _s, _t, _u;
                    const resp = (yield this.sendQrcCommand('ChangeGroup.Poll', {
                        Id: QSys.cgId,
                    }, ['poll']));
                    for (const change of (_q = (_p = resp.result) === null || _p === void 0 ? void 0 : _p.Changes) !== null && _q !== void 0 ? _q : []) {
                        let chan;
                        let paramType;
                        if (change.Component !== undefined) {
                            chan = this.channels.find(chan => chan.name === change.Component);
                            if (chan === undefined) {
                                this.log.error(`unrecognized change group control: ${change.Name}`);
                                continue;
                            }
                            paramType = this.getParamType(chan.type, change.Name);
                        }
                        else {
                            chan = this.channels.find(chan => chan.name === change.Name);
                            if (chan === undefined) {
                                this.log.error(`unrecognized change group control: ${change.Name}`);
                                continue;
                            }
                            paramType = this.getParamType(chan.type);
                        }
                        if (paramType === 'mute') {
                            this.state.mutes.update(chan.name, ((_r = change.Value) !== null && _r !== void 0 ? _r : 0) !== 0);
                        }
                        else if (paramType === 'receiveGain') {
                            let softPhoneStatus;
                            for (const [phoneName, gainName] of Object.entries(this.receiveGainNames)) {
                                if (chan.name === gainName) {
                                    softPhoneStatus = phoneName;
                                    break;
                                }
                            }
                            if (softPhoneStatus === undefined) {
                                this.log.error(`no receive gain for telephony channel: "${chan.name}"`);
                            }
                            else {
                                this.state.calls.updateReceiveGain(softPhoneStatus, (_s = change.Value) !== null && _s !== void 0 ? _s : 0);
                            }
                        }
                        else if (paramType === 'telephony') {
                            this.handleCallStatus(chan.name, (_t = change.String) !== null && _t !== void 0 ? _t : '');
                        }
                        else if (paramType === 'volume') {
                            this.state.volumes.update(chan.name, (_u = change.Value) !== null && _u !== void 0 ? _u : 0);
                        }
                    }
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.config = config;
            this.receiveGainNames = {};
            this.volumeChannel = undefined;
        });
    }
    onControl() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            yield this.sendQrcCommand('ChangeGroup.Destroy', { Id: QSys.cgId });
            for (const chan of this.channels) {
                if (chan.name in this.state.mutes.values) {
                    yield this.addToChangeGroup(chan, 'mute');
                }
                if (chan.name in this.state.volumes.values) {
                    yield this.addToChangeGroup(chan, 'volume');
                }
                for (const telConfig of Object.values(((_a = this.config.telephony) !== null && _a !== void 0 ? _a : {}))) {
                    if (chan.name === telConfig.softPhoneStatus) {
                        yield this.addToChangeGroup(chan, 'telephony');
                    }
                    if (chan.name === telConfig.receiveGain) {
                        yield this.addToChangeGroup(chan, 'receiveGain');
                    }
                }
            }
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.getStatus();
            }
            catch (err) {
                return 'verifyFailed';
            }
            return 'connected';
        });
    }
    getStatus() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.sendQrcCommand('StatusGet', 0);
        });
    }
    // other class methods
    addToChangeGroup(chan, paramType) {
        return __awaiter(this, void 0, void 0, function* () {
            if (chan.type.startsWith('comp')) {
                yield this.sendQrcCommand('ChangeGroup.AddComponentControl', {
                    Id: QSys.cgId,
                    Component: {
                        Name: chan.name,
                        Controls: [{ Name: this.getControlName(paramType, chan.type) }],
                    },
                });
            }
            else {
                yield this.sendQrcCommand('ChangeGroup.AddControl', {
                    Id: QSys.cgId,
                    Controls: [chan.name],
                });
            }
        });
    }
    getAvailableCallInterface(callType) {
        for (const [key, call] of Object.entries(this.state.calls.values)) {
            if (call.callType === callType && call.callState === 'disconnected')
                return key;
        }
        return undefined;
    }
    getControlName(paramType, chanType) {
        if (paramType === 'telephony') {
            return 'call_status';
        }
        else if (paramType === 'receiveGain') {
            if (chanType === 'comp.telephony_receiver') {
                return 'channel_1_gain';
            }
            else if (chanType === 'comp.io_card_pots_input_for_core') {
                return 'channel_1_upstream_input_gain';
            }
            else {
                return 'gain';
            }
        }
        else if (paramType === 'volume') {
            return 'gain';
        }
        else {
            return 'mute';
        }
    }
    getParamType(chanType, controlName) {
        if (chanType === 'ctrl.gain') {
            return 'volume';
        }
        else if (chanType === 'ctrl.mute') {
            return 'mute';
        }
        else if (chanType === 'comp.io_card_pots_input_for_core' &&
            controlName === 'channel_1_upstream_input_gain') {
            return 'receiveGain';
        }
        else if (chanType === 'comp.softphone') {
            return 'telephony';
        }
        else if (chanType === 'comp.pots_control_status') {
            return 'telephony';
        }
        else if (chanType === 'comp.telephony_receiver' && controlName === 'channel_1_gain') {
            return 'receiveGain';
        }
        else if (chanType === 'comp.gain') {
            if (controlName === 'mute') {
                return 'mute';
            }
            else if (controlName === 'gain') {
                return 'volume';
            }
        }
        this.log.error(`unexpected chanType: ${chanType}`);
        return 'mute';
    }
    handleCallStatus(key, value) {
        // Sometimes the emulator sends an empty call progress string, usually when first
        // connecting and registering for updates. We just ignore it.
        if (value === '')
            return;
        const chan = this.channels.find(chan => chan.name === key);
        if (chan === undefined) {
            this.log.error(`invalid key: ${key}`);
            return;
        }
        const callType = getChannelCallType(chan);
        if (callType === undefined) {
            this.log.error(`unknown call type for channel ${key}, type ${chan.type}`);
            return;
        }
        const call = this.state.calls.values[key];
        const match = /^([^-]+)(?:\s+-\s*(.*)?)?$/.exec(value);
        if (!match) {
            this.log.error(`call progress failed to match: ${value}`);
            return;
        }
        const callExtras = {};
        if (callType === 'voip' && typeof match[2] === 'string' && match[2] !== '') {
            callExtras.contactName = match[2];
        }
        if (callType === 'pstn' &&
            typeof match[2] === 'string' &&
            match[2] !== '' &&
            (call === null || call === void 0 ? void 0 : call.direction) !== 'incoming') {
            // Sometimes the call status message from the QSC contains the previous (incorrect) dial
            // string on an incoming call, thus the last case in the condition above.
            callExtras.dialString = match[2];
        }
        switch (match[1]) {
            case 'Waiting for Dial Tone': // pstn, seen when placing a call
            case 'Calibrating': // pstn, seen when placing a call
            case 'Line Fault': // pstn
            case 'Ready': // pstn, seen when placing a call
                break;
            case 'Connected': // voip, pstn
                this.state.calls.connect(key, callExtras);
                break;
            case 'Callee Ringing': // pstn
            case 'Dialing': // voip, pstn
                this.state.calls.setOutgoing(key, callType, callExtras);
                break;
            case 'Incoming Call': // voip, pstn
                this.state.calls.setIncoming(key, callExtras);
                break;
            case 'Call Failed': // voip
            case 'NO_ANSWER': // voip
            case 'Normal Clearing': // voip (emulator)
                // Ignore these states, they will be followed by a transition to Idle, which will
                // disconnect the call.
                break;
            case 'Disconnected': // voip
            case 'Idle': // voip, pstn
            case 'Initializing': // seen periodically in emulator
            case 'NO_ROUTE_DESTINATION': // voip, seen when dialing an unreachable ip address
            case 'NORMAL_TEMPORARY_FAILURE': // voip, seen when dialing invalid number
            case 'UNALLOCATED_NUMBER': // voip
                this.state.calls.disconnect(key, callExtras);
                break;
            default:
                this.log.error(`unexpected call progress state: "${value}"`);
                return;
        }
    }
    sendQrcCommand(method, params, tags = [], options) {
        return __awaiter(this, void 0, void 0, function* () {
            const id = this.id++;
            if (this.id > QSys.maxId)
                this.id = QSys.minId;
            return (yield this.socket.send(JSON.stringify({ jsonrpc: '2.0', id, method, params }), (resp) => {
                return resp.id === id ? resp : undefined;
            }, tags, options));
        });
    }
    validate(name, filter) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            for (const chan of this.channels) {
                if (chan.name === name) {
                    return chan.filters[filter] ? 'ok' : 'incompatible';
                }
            }
            try {
                const resp = (yield this.sendQrcCommand('Control.Get', [name], [`validate.${name}`]));
                let type = 'ctrl.unknown';
                if ('result' in resp || Array.isArray(resp.result)) {
                    if (['muted', 'unmuted', 'true', 'false'].includes((_b = (_a = resp.result) === null || _a === void 0 ? void 0 : _a[0].String) !== null && _b !== void 0 ? _b : '')) {
                        type = 'ctrl.mute';
                    }
                    else if (typeof ((_c = resp.result) === null || _c === void 0 ? void 0 : _c[0].Value) === 'number') {
                        type = 'ctrl.gain';
                    }
                    const chan = {
                        filters: getFilters(type),
                        name: name,
                        type,
                    };
                    this.channels.push(chan);
                    return chan.filters[filter] ? 'ok' : 'incompatible';
                }
            }
            catch (error) { }
            return 'missing';
        });
    }
}
// static properties
QSys.cgId = 'MiraConnect';
QSys.channelsConfigName = 'qSysControlChannels';
QSys.digitMap = {
    '0': 'call_pinpad_0',
    '1': 'call_pinpad_1',
    '2': 'call_pinpad_2',
    '3': 'call_pinpad_3',
    '4': 'call_pinpad_4',
    '5': 'call_pinpad_5',
    '6': 'call_pinpad_6',
    '7': 'call_pinpad_7',
    '8': 'call_pinpad_8',
    '9': 'call_pinpad_9',
    '*': 'call_pinpad_*',
    '.': 'call_pinpad_*',
    '#': 'call_pinpad_#',
};
QSys.driverType = 'socketDriver';
QSys.key = '-K_FswIwXRBjEqTRASYT';
QSys.minId = 1000;
QSys.maxId = 9999;
QSys.socketOptions = [
    {
        commandExtractor: {
            terminators: ['\0'],
        },
        parser: (command) => {
            try {
                const resp = JSON.parse(command);
                return resp;
            }
            catch (err) {
                return undefined;
            }
        },
        port: 1710,
        sendTerminator: '\0',
        sendTimeoutMs: 4000, // the QSys Designer emulator can be slow sometimes
    },
];
